<template>
  <div id="web-editor">
    <div class="edit-draft">
      <header class="editor-header">
        <input
          placeholder="输入文章标题..."
          spellcheck="false"
          maxlength="80"
          class="title-input"
          v-model="DetailInfo.title" />
        <div class="right-box">
          <div class="topic-icon"></div>
          <div class="status-text with-padding">
            {{ DetailInfo.author }} &nbsp;&nbsp;{{ DetailInfo.created_at }}
          </div>
          <div class="with-padding">
            <button
              class="publish-btn"
              @click="publishBtn">
              操作
            </button>
          </div>
        </div>
      </header>
    </div>
    <div class="md-editor">
      <Viewer
        ref="markDownRef"
        :locale="zh"
        :value="value"
        :plugins="plugins" />
    </div>
  </div>
  <el-dialog
    v-model="state.dialogFormVisible"
    title="审核文章"
    width="580">
    <div class="plusdivider"></div>
    <el-form
      ref="ruleFormRef"
      :model="form"
      :rules="rules">
      <el-form-item
        label="分类"
        :label-width="formLabelWidth"
        prop="audit_status">
        <el-radio-group v-model="form.audit_status">
          <el-radio
            :key="audit.value"
            :value="audit.value"
            v-for="audit in state.auditstatus"
            border
            >{{ audit.text }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item
        label="级别"
        :label-width="formLabelWidth"
        prop="category_id">
        <el-input-number v-model="form.article_level" :step="1" step-strictly />&nbsp;&nbsp;信息流显示4级以上
      </el-form-item>
      <el-form-item
        label="推荐"
        :label-width="formLabelWidth"
        prop="audit_status">
        <el-radio-group v-model="form.is_hot">
          <el-radio
            :key="0"
            :value="0"
            border
            >不推荐
          </el-radio>
          <el-radio
            :key="1"
            :value="1"
            border
            >推荐
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item class="button-group-right">
        <el-button
          type="primary"
          @click="submitForm(ruleFormRef)">
          确定
        </el-button>
        <el-button @click="state.dialogFormVisible = false"
          >取消</el-button
        >
      </el-form-item>
    </el-form>
  </el-dialog>
</template>

<script lang="ts" setup>
import { onMounted, reactive, toRefs, markRaw, ref } from "vue";

import { ElMessage } from "element-plus";
import { useRouter } from "vue-router";
import axiosInstance from "@/utils/axiosInstance";

import { Viewer } from "@bytemd/vue-next";

import gfm from "@bytemd/plugin-gfm";
import "bytemd/dist/index.css";

// 突出显示代码块
import highlight from "@bytemd/plugin-highlight";
import "highlight.js/styles/default.css";

// 对齐方式
import alignPlugin from "bytemd-plugin-align";
// 主题
import theme from "bytemd-plugin-theme";
// 解析前言 格式化
import frontmatter from "@bytemd/plugin-frontmatter";
// 表情
import gemoji from "@bytemd/plugin-gemoji";

// 代码高亮
import highLightPlugin from "bytemd-plugin-highlight";
// 图片的缩放
import imagePlugin from "bytemd-plugin-image";
// 代码复制
import copyCode from "bytemd-plugin-copy-code";
import "bytemd-plugin-copy-code/dist/style/index.css";

// 缩放图片 点击图片放大,再点击缩小
import mediumZoom from "@bytemd/plugin-medium-zoom";
// 图表
import mermaid from "@bytemd/plugin-mermaid";
import mermaidLocale from "@bytemd/plugin-mermaid/locales/zh_Hans.json";
// 数学公式插件
import math from "@bytemd/plugin-math";
import mathLocale from "@bytemd/plugin-math/locales/zh_Hans.json";
import "katex/dist/katex.css";

import zhHans from "bytemd/locales/zh_Hans.json";
import { useHead } from "@vueuse/head";
import { WebSite_Name } from "@/interface/globalindex";

import type { FormInstance, FormRules } from "element-plus";

const SSRData = ref({
  title: "",
  description: "",
  keywords:
    "宁波技术社区,默认代码,程序员中文技术社区,前端开发社区,前端技术交流,前端框架教程",
});

const formLabelWidth = "83px";

// 插件
const pluginsList = [
  gfm(),
  math({
    locale: mathLocale,
  }),
  alignPlugin(),
  highLightPlugin(),
  imagePlugin(),
  copyCode({
    copySuccess: (text) => {
      console.log(text);
      ElMessage.success("代码复制成功");
    },
    copyError: (err) => {
      console.log(err);
    },
  }),
  mediumZoom(),
  mermaid({
    locale: mermaidLocale,
  }),
  highlight(),
  theme(),
  frontmatter(),
  gemoji(),
];

const router = useRouter();

// 编辑器数据初始化 1 未通过 2 待审核  3 通过
const state = reactive({
  value: "",
  plugins: markRaw(pluginsList),
  zh: zhHans,
  isempty: ref(true),
  dialogFormVisible: ref(false),
  auditstatus:[{text:'未通过',value:1},{text:'待审核',value:2},{text:'通过',value:3}]
});

const { value, plugins, zh, markDownRef } = toRefs(state);

//文章信息
const DetailInfo = reactive({
  article_id: "",
  title: "",
  info_content: "",
  category_id: "",
  category_name: "",
  brief_content: "",
  link_url: "",
  cover_image: "",
  comment_count: "",
  view_count: "",
  digg_count: "",
  favorited_count: "",
  is_markdown: "",
  is_hot: "",
  uuid: "",
  author: "",
  created_at: "",
  avatar_url: "",
});

onMounted(async () => {
  //获取文章ID 参数
  const postID = router.currentRoute.value.params.postID as string;
  fetchArticleInfoData(postID);
});

// 获取文章数据
const fetchArticleInfoData = async (postID: string) => {
  try {
    await axiosInstance
      .get("/api/HomeArticle/detail", {
        params: { article_id: postID },
      })
      .then((res) => {
        var result = res.data;
        if (result.success) {
          const articleData = result.data;
          DetailInfo.article_id = articleData.article_id;
          DetailInfo.title = articleData.title;
          DetailInfo.info_content = articleData.info_content;
          DetailInfo.category_id = articleData.category_id;
          DetailInfo.category_name = articleData.category_name;
          DetailInfo.brief_content = articleData.brief_content;
          DetailInfo.link_url = articleData.link_url;
          DetailInfo.cover_image = articleData.cover_image;
          DetailInfo.comment_count = articleData.comment_count;
          DetailInfo.view_count = articleData.view_count;
          DetailInfo.digg_count = articleData.digg_count;
          DetailInfo.favorited_count = articleData.favorited_count;
          DetailInfo.is_markdown = articleData.is_markdown;
          DetailInfo.is_hot = articleData.is_hot;
          DetailInfo.uuid = articleData.uuid;
          DetailInfo.author = articleData.author;
          DetailInfo.avatar_url = articleData.avatar_url;
          DetailInfo.created_at = articleData.created_at;
          state.value = articleData.info_content;
          //SEO 内容
          SSRData.value.title = articleData.title + " - " + WebSite_Name;
          SSRData.value.description = articleData.brief_content;
        } else {
          state.isempty = false;
          //SEO 内容
          SSRData.value.title = "找不到页面";
          SSRData.value.description = "";
          SSRData.value.keywords = "";
        }
        setSSRHead();
      });
  } catch (error) {
    ElMessage.error("获取文章数据失败");
  }
};

//动态设置SEO 信息
const setSSRHead = () => {
  useHead({
    title: SSRData.value.title,
    meta: [
      { name: "description", content: SSRData.value.description },
      { name: "keywords", content: SSRData.value.keywords },
    ],
  });
};

//操作
const publishBtn = () => {
  state.dialogFormVisible = true;
};

interface RuleForm {
  article_id: string;
  audit_status: Number;
  article_level: Number;
  is_hot: Number;
}
const ruleFormRef = ref<FormInstance>();

const form = reactive<RuleForm>({
  article_id: "",
  audit_status: 1,
  article_level: 4,
  is_hot:0
});

const rules = reactive<FormRules<RuleForm>>({
   audit_status: [{ required: true, message: "请选择审核类型", trigger: "blur" }],
});

const submitForm = (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  formEl.validate((valid) => {
    if (valid) {
      let params = {
        article_id: DetailInfo.article_id,
        audit_status:form.audit_status,
        article_level:form.article_level,
        is_hot:form.is_hot
      };

      axiosInstance
        .post("api/EditorialCheckList/AuditArticleSave", params)
        .then((res) => {
          var result = res.data;
          if (result.success) {
            router.push({ path: "/creator/postreview"});
          }
        });
    } else {
      console.log("error submit!");
    }
  });
};

</script>

<style scoped>
#web-editor {
  display: block;
  height: 100vh;
}

.editor-header {
  padding: 0 27px;
  height: 63px;
  background-color: #fff;
  border-bottom: 1px solid #ddd;
  z-index: 100;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  padding-right: 14px;
  cursor: pointer;
}

.title-input {
  -webkit-box-flex: 1;
  -ms-flex: 1 1 auto;
  flex: 1 1 auto;
  height: 100%;
  margin: 0;
  padding: 0;
  font-size: 24px;
  font-weight: 500;
  color: #1d2129;
  border: none;
  outline: none;
}

.right-box {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  -webkit-box-pack: end;
  -ms-flex-pack: end;
  justify-content: flex-end;
  margin-right: 100px;
}
.topic-icon {
  margin-right: 40px;
}
.with-padding {
  margin-left: 8px;
  margin-right: 8px;
}
.status-text {
  font-size: 14px;
  white-space: nowrap;
  color: #000;
  cursor: default;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.btn-drafts {
  height: 32px;
  padding: 2px 16px;
  font-size: 14px;
  line-height: 22px;
  border: 1px solid #cc5656;
  border-radius: 2px;
  cursor: pointer;
  color: #cc5656;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  background-color: #fff;
  margin-right: 20px;
}
.with-padding {
  margin-left: 8px;
  margin-right: 8px;
}
.publish-btn {
  height: 32px;
  padding: 2px 16px;
  font-size: 14px;
  line-height: 22px;
  border: 1px solid #cc5656;
  border-radius: 2px;
  cursor: pointer;
  color: #fff;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  background-color: #cc5656;
}

.with-padding:last-child {
  padding-right: 0;
}
.navigator {
  position: relative;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.toggle-btn {
  width: 2.667rem;
  height: 2.667rem;
  border-radius: 50%;
  background-color: #eee;
  background-position: 50%;
  background-size: cover;
  background-repeat: no-repeat;
  cursor: pointer;
}
.md-editor {
  width: 720px;
  margin: 0 auto;
  background-color: #fff;
  padding:10px;
}
.md-editor ::v-deep .bytemd {
  height: calc(100vh - 5.334rem);
}
.md-editor ::v-deep .cm-s-default .cm-variable-2 {
  color: #000;
}
.button-group-right ::v-deep(.el-form-item__content) {
  display: flex;
  justify-content: flex-end;
}

.avatar-uploader ::v-deep .el-upload {
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: 0.2s;
}
.avatar-uploader ::v-deep .el-upload:hover {
  border-color: #409eff;
}
.el-icon.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  text-align: center;
}
.upload-text {
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: #86909c;
  display: block;
  width: 100%;
}
::v-deep .el-form-item__label {
  width: 85px;
}
.plusdivider {
  width: 100%;
  height: 1px;
  border-bottom: 1px solid #dcdfe6;
  margin-bottom: 20px;
}

::v-deep .markdown-body img {
  max-width: 100%;
}

::v-deep .el-radio__inner {
  display: none;
}
::v-deep .el-radio {
  width: 98px;
  margin-right: 9px;
  margin-bottom: 10px;
}

/* 编辑器相关样式 */
::v-deep .markdown-body img {
  max-width: 100%;
}

::v-deep .el-radio__inner {
  display: none;
}
::v-deep .el-radio {
  width: 98px;
  margin-right: 9px;
  margin-bottom: 10px;
}
::v-deep .markdown-body {
  word-break: break-word;
  line-height: 1.75;
  font-weight: 400;
  font-size: 16px;
  overflow-x: hidden;
  color: #252933;
}
::v-deep .markdown-body h1,
::v-deep .markdown-body h2,
::v-deep .markdown-body h3,
::v-deep .markdown-body h4,
::v-deep .markdown-body h5,
::v-deep .markdown-body h6 {
  line-height: 1.5;
  margin-top: 35px;
  margin-bottom: 10px;
  padding-bottom: 5px;
}
::v-deep .markdown-body h1 {
  font-size: 24px;
  line-height: 38px;
  margin-bottom: 5px;
}
::v-deep .markdown-body h2 {
  font-size: 22px;
  line-height: 34px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ececec;
}
::v-deep .markdown-body h3 {
  font-size: 20px;
  line-height: 28px;
}
::v-deep .markdown-body h4 {
  font-size: 18px;
  line-height: 26px;
}
::v-deep .markdown-body h5 {
  font-size: 17px;
  line-height: 24px;
}
::v-deep .markdown-body h6 {
  font-size: 16px;
  line-height: 24px;
}
::v-deep .markdown-body p {
  line-height: inherit;
  margin-top: 22px;
  margin-bottom: 22px;
}
::v-deep .markdown-body img {
  max-width: 100%;
}
::v-deep .markdown-body hr {
  border: none;
  border-top: 1px solid #ddd;
  margin-top: 32px;
  margin-bottom: 32px;
}
::v-deep .markdown-body code {
  word-break: break-word;
  border-radius: 2px;
  overflow-x: auto;
  background-color: #fff5f5;
  color: #ff502c;
  font-size: 0.87em;
  padding: 0.065em 0.4em;
}
::v-deep .markdown-body code,
::v-deep .markdown-body pre {
  font-family: Menlo, Monaco, Consolas, Courier New, monospace;
}
::v-deep .markdown-body pre {
  overflow: auto;
  position: relative;
  line-height: 1.75;
}
::v-deep .markdown-body pre > code {
  font-size: 12px;
  padding: 15px 12px;
  margin: 0;
  word-break: normal;
  display: block;
  overflow-x: auto;
  color: #333;
  background: #f8f8f8;
}
::v-deep .markdown-body a {
  text-decoration: none;
  color: #0269c8;
  border-bottom: 1px solid #d1e9ff;
}
::v-deep .markdown-body a:active,
::v-deep .markdown-body a:hover {
  color: #275b8c;
}
::v-deep .markdown-body table {
  display: inline-block !important;
  font-size: 12px;
  width: auto;
  max-width: 100%;
  overflow: auto;
  border: 1px solid #f6f6f6;
}
::v-deep .markdown-body thead {
  background: #f6f6f6;
  color: #000;
  text-align: left;
}
::v-deep .markdown-body tr:nth-child(2n) {
  background-color: #fcfcfc;
}
::v-deep .markdown-body td,
::v-deep .markdown-body th {
  padding: 12px 7px;
  line-height: 24px;
}
::v-deep .markdown-body td {
  min-width: 120px;
}
::v-deep .markdown-body blockquote {
  color: #666;
  padding: 1px 23px;
  margin: 22px 0;
  border-left: 4px solid #cbcbcb;
  background-color: #f8f8f8;
}
::v-deep .markdown-body blockquote:after {
  display: block;
  content: "";
}
::v-deep .markdown-body blockquote > p {
  margin: 10px 0;
}
::v-deep .markdown-body ol,
::v-deep .markdown-body ul {
  padding-left: 28px;
}
::v-deep .markdown-body ol li,
::v-deep .markdown-body ul li {
  margin-bottom: 0;
  list-style: inherit;
}
::v-deep .markdown-body ol li .task-list-item,
::v-deep .markdown-body ul li .task-list-item {
  list-style: none;
}
::v-deep .markdown-body ol li .task-list-item ol,
::v-deep .markdown-body ol li .task-list-item ul,
::v-deep .markdown-body ul li .task-list-item ol,
::v-deep .markdown-body ul li .task-list-item ul {
  margin-top: 0;
}
::v-deep .markdown-body ol ol,
::v-deep .markdown-body ol ul,
::v-deep .markdown-body ul ol,
::v-deep .markdown-body ul ul {
  margin-top: 3px;
}
::v-deep .markdown-body ol li {
  padding-left: 6px;
}
::v-deep .markdown-body .contains-task-list {
  padding-left: 0;
}
::v-deep .markdown-body .task-list-item {
  list-style: none;
}
@media (max-width: 720px) {
  ::v-deep .markdown-body h1 {
    font-size: 24px;
  }
  ::v-deep .markdown-body h2 {
    font-size: 20px;
  }
  ::v-deep .markdown-body h3 {
    font-size: 18px;
  }
}
</style>
